import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase';

const PAGE_SIZE = 20;

/**
 * Hook for fetching researcher profiles with cursor-based pagination
 */
export const useResearchers = () => {
  const [researchers, setResearchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const lastDocRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setResearchers([]);
    lastDocRef.current = null;
    setHasMore(true);

    const q = query(
      collection(db, 'researcherProfiles'),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE)
    );

    getDocs(q)
      .then((snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setResearchers(data);
        lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
        setHasMore(snapshot.docs.length === PAGE_SIZE);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching researcher profiles:', err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !lastDocRef.current) return;
    setLoadingMore(true);

    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'researcherProfiles'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDocRef.current),
          limit(PAGE_SIZE)
        )
      );
      const newData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setResearchers((prev) => [...prev, ...newData]);
      lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more researcher profiles:', err);
      setError(err.message);
    }
    setLoadingMore(false);
  }, [loadingMore, hasMore]);

  return { researchers, loading, loadingMore, hasMore, loadMore, error };
};

/**
 * Hook for fetching a single researcher profile by UID
 */
export const useResearcherProfile = (uid) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    getDoc(doc(db, 'researcherProfiles', uid))
      .then((snap) => {
        setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [uid]);

  return { profile, loading };
};

/**
 * Save (create or update) a researcher profile
 */
export const saveResearcherProfile = async (uid, data, isNew) => {
  const profileDocRef = doc(db, 'researcherProfiles', uid);
  await setDoc(profileDocRef, {
    ...data,
    uid,
    updatedAt: serverTimestamp(),
    ...(isNew ? { createdAt: serverTimestamp() } : {}),
  }, { merge: true });

  // Save researcher profile reference on the user document
  await updateDoc(doc(db, 'users', uid), {
    researcherProfileId: uid,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete (unpublish) a researcher profile
 */
export const deleteResearcherProfile = async (uid) => {
  await deleteDoc(doc(db, 'researcherProfiles', uid));

  // Remove researcher profile reference from the user document
  await updateDoc(doc(db, 'users', uid), {
    researcherProfileId: null,
    updatedAt: serverTimestamp(),
  });
};
